import { z } from 'zod';
import { CALENDAR_YEAR_MAX, CALENDAR_YEAR_MIN, daysInMonth, type LocalDate } from './birthday.js';
import { type Locale } from './constants.js';

/**
 * Company events in the calendar (spec §6.13, §6.17): created by admins, shown to everyone next
 * to the birthdays. A date is a plain office-local calendar day (`YYYY-MM-DD`), no time or zone.
 */

export const CALENDAR_EVENT_TYPES = ['event', 'social', 'holiday'] as const;
export type CalendarEventType = (typeof CALENDAR_EVENT_TYPES)[number];

export const CALENDAR_EVENT_TITLE_MAX = 80;

/** Titles in both UI languages; `sv` falls back to `en` when empty. */
export interface CalendarEventTitle {
  readonly en: string;
  readonly sv: string;
}

export interface CalendarEvent {
  readonly id: string;
  /** `YYYY-MM-DD` in the office time zone. */
  readonly date: string;
  readonly title: CalendarEventTitle;
  readonly type: CalendarEventType;
}

/** One event in a month view; `day` is the day of the month of `date`. */
export interface CalendarMonthEvent extends CalendarEvent {
  readonly day: number;
}

const DATE_PATTERN = /^(\d{4})-(\d{2})-(\d{2})$/;

/** Parses a `YYYY-MM-DD` string; `undefined` when malformed or no such day. */
export function parseIsoDate(value: string): LocalDate | undefined {
  const match = DATE_PATTERN.exec(value);
  if (!match) return undefined;
  const [year, month, day] = [Number(match[1]), Number(match[2]), Number(match[3])];
  if (month < 1 || month > 12 || day < 1 || day > daysInMonth(year, month)) return undefined;
  return { year, month, day };
}

/** `{2026, 3, 7}` → `2026-03-07`. */
export function formatIsoDate({ year, month, day }: LocalDate): string {
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** First and last day of a month as `YYYY-MM-DD`, for range queries. */
export function monthRange(year: number, month: number): { from: string; to: string } {
  return {
    from: formatIsoDate({ year, month, day: 1 }),
    to: formatIsoDate({ year, month, day: daysInMonth(year, month) }),
  };
}

export const calendarEventDateSchema = z.string().refine((value) => {
  const date = parseIsoDate(value);
  return date !== undefined && date.year >= CALENDAR_YEAR_MIN && date.year <= CALENDAR_YEAR_MAX;
}, 'Expected a date YYYY-MM-DD');

const titleText = z
  .string()
  .trim()
  .max(CALENDAR_EVENT_TITLE_MAX)
  .refine((value) => !/[\p{Cc}<>]/u.test(value), 'Contains forbidden characters');

export const calendarEventTitleSchema = z
  .object({ en: titleText.pipe(z.string().min(1)), sv: titleText.default('') })
  .strict();

export const createCalendarEventSchema = z
  .object({
    date: calendarEventDateSchema,
    title: calendarEventTitleSchema,
    type: z.enum(CALENDAR_EVENT_TYPES).default('event'),
  })
  .strict();
export type CreateCalendarEventRequest = z.input<typeof createCalendarEventSchema>;

export const updateCalendarEventSchema = z
  .object({
    date: calendarEventDateSchema.optional(),
    title: calendarEventTitleSchema.optional(),
    type: z.enum(CALENDAR_EVENT_TYPES).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, 'Nothing to update');
export type UpdateCalendarEventRequest = z.input<typeof updateCalendarEventSchema>;

/** Query of `GET /api/admin/calendar/events`: events on or after `from` (default: today). */
export const adminCalendarEventsQuerySchema = z.object({
  from: calendarEventDateSchema.optional(),
});

/** The title in `locale`, falling back to English when the Swedish one is empty. */
export function eventTitle(title: CalendarEventTitle, locale: Locale | string): string {
  return locale === 'sv' && title.sv.trim() ? title.sv : title.en;
}

/** Events in a month, sorted by day, then English title. */
export function eventsInMonth(
  events: readonly CalendarEvent[],
  year: number,
  month: number,
): CalendarMonthEvent[] {
  return events
    .flatMap((event) => {
      const date = parseIsoDate(event.date);
      return date && date.year === year && date.month === month
        ? [{ ...event, day: date.day }]
        : [];
    })
    .sort((a, b) => a.day - b.day || a.title.en.localeCompare(b.title.en));
}
